/** Angular Imports */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertService } from 'app/core/alert/alert.service';
import { Dates } from 'app/core/utils/dates';

/** rxjs Imports */
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** Environment Imports */
import { environment } from '../../environments/environment';

/**
 * Tenant display object used by the tenant selector dropdown.
 */
export interface TenantDisplay {
  identifier: string;
  name: string;
}

/**
 * Settings Service
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private alertService = inject(AlertService);
  private dateUtils = inject(Dates);
  private http = inject(HttpClient);

  public static businessDateFormat = 'yyyy-MM-dd';
  public static businessDateConfigName = 'enable-business-date';
  public static businessDateType = 'BUSINESS_DATE';
  public static cobDateType = 'COB_DATE';
  minAllowedDate = new Date(1950, 0, 1);
  maxAllowedDate = new Date(2100, 0, 1);

  /**
   * Sets date format setting throughout the app.
   * @param {string} dateFormat Date Format
   */
  setDateFormat(dateFormat: string) {
    localStorage.setItem('LOSXDateFormat', JSON.stringify(dateFormat));
  }

  /**
   * Sets language setting throughout the app.
   * @param {any} language Language.
   */
  setLanguage(language: { name: string; code: string }) {
    localStorage.setItem('LOSXLanguage', JSON.stringify(language));
  }

  /**
   * Sets decimals to Display throughout the app.
   * @param {string} decimals.
   */
  setDecimalToDisplay(decimals: string) {
    localStorage.setItem('LOSXDecimalsToDisplay', decimals);
  }

  setDefaultLanguage() {
    const defaultLanguage = environment.defaultLanguage ? environment.defaultLanguage : 'en-US';
    this.setLanguage({
      name: defaultLanguage,
      code: defaultLanguage.substring(0, 2)
    });
  }

  /**
   * Sets server URL setting throughout the app.
   * @param {string} url URL
   */
  setServer(url: string) {
    localStorage.setItem('LOSXServerURL', url);
  }

  /**
   * Sets server URL setting throughout the app.
   * @param {string[]} list List of default servers
   */
  setServers(list: string[]) {
    localStorage.setItem('LOSXServers', JSON.stringify(list));
  }

  /**
   * Sets Tenant Identifiers list setting throughout the app.
   * @param {string[]} list List of default tenants
   */
  setTenantIdentifiers(list: string[]) {
    localStorage.setItem('LOSXTenantIdentifiers', JSON.stringify(list));
  }

  /**
   * Sets full tenant objects (identifier + name) for display in the tenant selector.
   * @param {TenantDisplay[]} list List of tenant display objects.
   */
  setTenants(list: TenantDisplay[]) {
    localStorage.setItem('LOSXTenants', JSON.stringify(list));
  }

  /**
   * Fetches the list of available tenants from the backend and stores it.
   * Falls back to the static environment list if the backend call fails.
   * @return {Observable<TenantDisplay[]>} Observable of tenant display objects.
   */
  loadTenantIdentifiers(): Observable<TenantDisplay[]> {
    return this.http.get<any[]>('/tenants/public').pipe(
      map((tenants) => {
        const displayTenants = tenants
          .filter((tenant) => tenant.identifier)
          .map((tenant) => ({ identifier: tenant.identifier, name: tenant.name }));
        this.setTenantIdentifiers(displayTenants.map((tenant) => tenant.identifier));
        this.setTenants(displayTenants);
        return displayTenants;
      }),
      catchError(() => {
        const fallbackIdentifiers = (environment.fineractPlatformTenantIds || 'default').split(',');
        const fallbackTenants = fallbackIdentifiers.map((identifier) => ({ identifier, name: identifier }));
        this.setTenantIdentifiers(fallbackIdentifiers);
        this.setTenants(fallbackTenants);
        return of(fallbackTenants);
      })
    );
  }

  /**
   * Sets Tenant Identifier setting throughout the app.
   * @param {string} Tenant Identifier
   */
  setTenantIdentifier(tenantIdentifier: string) {
    localStorage.setItem('LOSXTenantIdentifier', tenantIdentifier);
  }

  /**
   * Sets server Date setting for max datepicker, default today
   * @param {string} date
   */
  setBusinessDate(date: string) {
    localStorage.setItem('LOSXServerDate', date);
  }

  /**
   * Sets server Date config is enabled
   * @param {string} enabled
   */
  setBusinessDateConfig(enabled: string) {
    localStorage.setItem('LOSXServerBusinessDateEnabled', enabled);
  }

  /**
   * Returns date format setting.
   */
  get dateFormat() {
    return JSON.parse(localStorage.getItem('LOSXDateFormat'));
  }

  /**
   * Returns language setting
   */
  get language() {
    if (!localStorage.getItem('LOSXLanguage')) {
      this.setDefaultLanguage();
    }
    return JSON.parse(localStorage.getItem('LOSXLanguage'));
  }

  get languageCode() {
    const currentLanguage = this.language.code;
    if (currentLanguage === 'es') {
      return 'es-MX';
    }
    if (currentLanguage === 'en') {
      return 'en-US';
    }
    return currentLanguage + '-' + currentLanguage.toUpperCase();
  }

  /**
   * Returns Decimals to Display setting
   */
  get decimals() {
    if (!localStorage.getItem('LOSXDecimalsToDisplay')) {
      return '2';
    }
    return localStorage.getItem('LOSXDecimalsToDisplay');
  }

  /**
   * Returns list of default server
   */
  get servers() {
    return JSON.parse(localStorage.getItem('LOSXServers'));
  }

  /**
   * Returns server setting
   */
  get server() {
    if (localStorage.getItem('LOSXServerURL')) {
      return localStorage.getItem('LOSXServerURL');
    }
    if (environment.baseApiUrl && environment.baseApiUrl !== '') {
      return environment.baseApiUrl;
    } else {
      return this.servers?.[0];
    }
  }

  /**
   * Returns server url with api path without version
   */
  get baseServerUrl() {
    return this.server + environment.apiProvider;
  }

  /**
   * Returns server url with api path and version
   */
  get serverUrl() {
    return this.server + environment.apiProvider + environment.apiVersion;
  }

  /**
   * Returns server url with api path and version
   */
  get serverHost() {
    return this.server;
  }

  /**
   * Returns current Business date server
   */
  get businessDate(): Date {
    return this.dateUtils.convertToDate(localStorage.getItem('LOSXServerDate'), SettingsService.businessDateFormat);
  }

  /**
   * Returns current Business date Config if it's enabled
   */
  get businessDateConfig(): any {
    return localStorage.getItem('LOSXServerBusinessDateEnabled');
  }

  /**
   * Returns min Past date
   */
  get minPastDate(): Date {
    return this.minAllowedDate;
  }

  /**
   * Returns max Future date
   */
  get maxFutureDate(): Date {
    return this.maxAllowedDate;
  }

  /**
   * Returns list of Tenant Identifiers
   */
  get tenantIdentifiers(): any {
    return JSON.parse(localStorage.getItem('LOSXTenantIdentifiers'));
  }

  /**
   * Returns full tenant display objects (identifier + name) for the tenant selector.
   */
  get tenants(): TenantDisplay[] {
    const stored = localStorage.getItem('LOSXTenants');
    if (stored) {
      return JSON.parse(stored);
    }
    const identifiers = this.tenantIdentifiers || [];
    return identifiers.map((identifier: string) => ({ identifier, name: identifier }));
  }

  /**
   * Returns Tenant Identifier
   */
  get tenantIdentifier(): string {
    return localStorage.getItem('LOSXTenantIdentifier');
  }

  /**
   * Validate If the enable_business_date configuration is enabled or disabled.
   */
  validateBusinessDateStatus(configurations: any) {
    configurations.some((config: any) => {
      if (config.name === SettingsService.businessDateConfigName) {
        return config.enabled;
      }
    });
  }

  /**
   * Get the Business Date or COB Date.
   */
  getBusinessDates(businessDateData: any, dateType: string): void {
    businessDateData.some((data: any) => {
      if (data.type === dateType) {
        const dateVal = new Date(data.date);
        this.setBusinessDate(this.dateUtils.formatDate(dateVal, SettingsService.businessDateFormat));
        this.alertService.alert({
          type: dateType + ' Set',
          message: this.dateUtils.formatDate(dateVal, this.dateFormat)
        });
        return;
      }
    });
  }

  setThemeDarkEnabled(enabled: boolean) {
    localStorage.setItem('LOSXThemeDarkEnabled', JSON.stringify(enabled));
  }

  get themeDarkEnabled(): boolean {
    return JSON.parse(localStorage.getItem('LOSXThemeDarkEnabled'));
  }
}
